import React from 'react';

import Avatar from '@atlaskit/avatar';
import FolderFilledIcon from '@atlaskit/icon/core/folder-closed';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import FolderFilledIconLegacy from '@atlaskit/icon/glyph/folder-filled';
import { SkeletonItem } from '@atlaskit/menu';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { gridSize } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	ProductIcon,
	ShortProductKey,
	useAnalytics,
} from '@atlassian/ptc-common';

import { type PlaceItem } from '../ProfilePlaces/types';

import * as Styled from './ProfilePlacesItem.styled';

export const PlaceLink = Styled.PlaceLink;

const MainIcon = ({ icon, type }: PlaceItem) => {
	if (icon) {
		return (
			<Styled.BaseIcon>
				{/* If Avatar cannot load image from `iconUrl`, it will show a default icon (a ship icon) */}
				<Avatar size="small" src={icon} appearance="square" />
			</Styled.BaseIcon>
		);
	}

	if (type === 'space') {
		return (
			<Styled.SpaceIcon>
				<FolderFilledIcon
					LEGACY_fallbackIcon={FolderFilledIconLegacy}
					color="currentColor"
					LEGACY_size="medium"
					spacing="spacious"
					label=""
				/>
			</Styled.SpaceIcon>
		);
	} else if (type === 'board') {
		return (
			<Styled.BoardIcon>
				<BoardIcon color="currentColor" LEGACY_size="medium" spacing="spacious" label="" />
			</Styled.BoardIcon>
		);
	}

	return null;
};

const HeaderIcon = ({ type }: Pick<PlaceItem, 'type'>) => {
	switch (type) {
		case 'space':
			return <ProductIcon size="xsmall" productKey={ShortProductKey.CONFLUENCE} />;
		case 'board':
		case 'project':
			return <ProductIcon size="xsmall" productKey={ShortProductKey.JIRA} />;
		default:
			return null;
	}
};

function typeToTitle(type: 'space' | 'board' | 'project') {
	switch (type) {
		case 'space':
			return 'Confluence';
		case 'board':
		case 'project':
			return 'Jira';
		default:
			return '';
	}
}

export const ProfilePlacesItem = (props: PlaceItem) => {
	const analytics = useAnalytics();

	const { name, path, type, hasLoaded } = props;

	const handleAnchorClick = () => {
		analytics.pushUIEvent({
			source: AnalyticsEventSource.USER_PROFILE_SCREEN,
			actionSubject: 'placesLink',
			action: AnalyticsEventActions.CLICKED,
			attributes: {
				type,
			},
		});
	};

	return (
		<Styled.PlacesItem>
			{hasLoaded ? (
				<>
					<Styled.Header>
						<Styled.HeaderIcon>
							<HeaderIcon type={type} />
						</Styled.HeaderIcon>
						<Styled.HeaderTitle>{typeToTitle(type)}</Styled.HeaderTitle>
					</Styled.Header>
					<Styled.Content>
						<MainIcon {...props} />
						<Styled.Details>
							<PlaceLink href={path} onClick={handleAnchorClick}>
								{name}
							</PlaceLink>
						</Styled.Details>
					</Styled.Content>
				</>
			) : (
				<>
					<SkeletonItem
						isShimmering
						// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
						cssFn={() => ({
							minHeight: gridSize() * 2,
							width: gridSize() * 18.75,
							marginBottom: token('space.050', '4px'),
							paddingLeft: 0,
							paddingRight: 0,
						})}
					/>
					<SkeletonItem
						hasIcon
						isShimmering
						// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
						cssFn={() => ({
							minHeight: gridSize() * 3.5,
							paddingLeft: 0,
							paddingRight: 0,
						})}
					/>
				</>
			)}
		</Styled.PlacesItem>
	);
};

export default ProfilePlacesItem;
